import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  LifeBuoy, PlusCircle, ListChecks, ClipboardList, Headphones,
  Monitor, Package, Calendar, BarChart3, ArrowRight, Clock,
  CheckCircle2, AlertCircle, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPT_ICONS = {
  IT: Monitor,
  Logistics: Package,
  Security: LifeBuoy,
  Catering: LifeBuoy,
  Medical: Calendar,
  OfficeSupply: Package,
};

const DEPT_COLORS = {
  IT: "from-blue-500 to-blue-600",
  Logistics: "from-amber-500 to-orange-600",
  Security: "from-red-500 to-red-600",
  Catering: "from-green-500 to-emerald-600",
  Medical: "from-cyan-500 to-teal-600",
  OfficeSupply: "from-purple-500 to-violet-600",
};

export default function RequestServiceDashboard() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/request/dashboard`, { headers });
        setStats(res.data);
      } catch (e) {
        console.error("Dashboard fetch error:", e);
        toast.error("Failed to load dashboard");
      }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  const departments = ["IT", "Logistics", "Security", "Catering", "Medical", "OfficeSupply"];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800">
          Request Service Hub
        </h1>
        <p className="text-slate-500 mt-1">
          Submit and track service requests across all departments
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">My Requests</p>
          <p className="text-3xl font-bold text-slate-800">{stats?.my_requests ?? 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">My Assigned</p>
          <p className="text-3xl font-bold text-amber-600">{stats?.my_assigned ?? 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Submitted</p>
          <p className="text-3xl font-bold text-slate-800">{stats?.submitted ?? 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">In Progress</p>
          <p className="text-3xl font-bold text-blue-600">{stats?.in_progress ?? 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Completed</p>
          <p className="text-3xl font-bold text-emerald-600">{stats?.completed ?? 0}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Link
          to="/request-service/new-request"
          className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
              <PlusCircle className="w-6 h-6 text-teal-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-800">New Service Request</h3>
              <p className="text-sm text-slate-500">Submit a request for any department</p>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-teal-500 transition-colors" />
          </div>
        </Link>
        <Link
          to="/request-service/my-requests"
          className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <ListChecks className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-800">My Requests</h3>
              <p className="text-sm text-slate-500">Track all your submitted requests</p>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 transition-colors" />
          </div>
        </Link>
      </div>

      {/* Department Quick Links */}
      <div className="mb-6">
        <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-slate-800 mb-4">
          Service Departments
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {departments.map((dept) => {
            const Icon = DEPT_ICONS[dept] || LifeBuoy;
            return (
              <button
                key={dept}
                onClick={() => navigate(`/request-service/new-request?department=${dept}`)}
                className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-all text-center group"
              >
                <div
                  className={`w-10 h-10 bg-gradient-to-br ${DEPT_COLORS[dept]} rounded-lg flex items-center justify-center mx-auto mb-2`}
                >
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <p className="font-medium text-sm text-slate-700">{dept === "OfficeSupply" ? "Office Supply" : dept}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Status Summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-slate-800 mb-4">
          Request Status Overview
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: "Submitted", value: stats?.submitted ?? 0, color: "text-slate-600", bg: "bg-slate-100" },
            { label: "Acknowledged", value: stats?.acknowledged ?? 0, color: "text-blue-600", bg: "bg-blue-100" },
            { label: "In Progress", value: stats?.in_progress ?? 0, color: "text-amber-600", bg: "bg-amber-100" },
            { label: "Completed", value: stats?.completed ?? 0, color: "text-emerald-600", bg: "bg-emerald-100" },
            { label: "Reopened", value: stats?.reopened ?? 0, color: "text-red-600", bg: "bg-red-100" },
          ].map((s) => (
            <div key={s.label} className={`${s.bg} rounded-lg p-4 text-center`}>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-slate-600 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
